/**
 * AdminTopbar — ui-web TopBar (M6-01) carrying: nav collapse toggle + page
 * title at the start; locale switch, theme-mode cycle and the account menu at
 * the end. The account menu is a hand-rolled disclosure (composition of
 * primitives, not a new primitive): Escape and outside-click close it, the
 * trigger carries aria-expanded/aria-haspopup (§14.3 accessibility).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CircleUserRound, LogOut, Monitor, Moon, PanelLeftClose, PanelLeftOpen, Sun } from "lucide-react";
import { Button, IconButton, TopBar, useTheme, type ThemeMode } from "@platform/ui-web";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { navItemForPath } from "./nav";

const MODE_ORDER: ReadonlyArray<ThemeMode> = ["light", "dark", "system"];

function ModeToggle() {
  const { mode, setMode } = useTheme();
  const { t } = useI18n();

  const next = MODE_ORDER[(MODE_ORDER.indexOf(mode) + 1) % MODE_ORDER.length] ?? "system";
  const label =
    mode === "light" ? t("topbar.theme.light") : mode === "dark" ? t("topbar.theme.dark") : t("topbar.theme.system");

  return (
    <IconButton
      size="sm"
      label={label}
      title={label}
      onClick={() => {
        setMode(next);
      }}
    >
      {mode === "light" ? <Sun size={18} /> : mode === "dark" ? <Moon size={18} /> : <Monitor size={18} />}
    </IconButton>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) close();
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") close();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  function handleLogout(): void {
    if (loggingOut) return;
    setLoggingOut(true);
    void (async () => {
      try {
        await logout();
      } finally {
        setLoggingOut(false);
      }
    })();
  }

  return (
    <div className="shell-account" ref={rootRef}>
      <button
        type="button"
        className="shell-account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("topbar.userMenu")}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <CircleUserRound size={20} aria-hidden="true" />
        <span className="shell-account-name">{user?.name}</span>
      </button>

      {open ? (
        <div className="shell-account-menu" role="menu" aria-label={t("topbar.userMenu")}>
          <div className="shell-account-id">
            <span className="shell-account-id-label">{t("topbar.signedInAs")}</span>
            <span className="shell-account-id-name">{user?.name}</span>
            <span className="shell-account-id-phone" dir="ltr">
              {user?.phone}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            role="menuitem"
            className="shell-account-logout"
            iconStart={<LogOut size={16} />}
            loading={loggingOut}
            onClick={handleLogout}
          >
            {loggingOut ? t("topbar.loggingOut") : t("topbar.logout")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export interface AdminTopbarProps {
  readonly navCollapsed: boolean;
  readonly onToggleNav: () => void;
}

export function AdminTopbar({ navCollapsed, onToggleNav }: AdminTopbarProps) {
  const { t, locale, setLocale } = useI18n();
  const location = useLocation();
  const active = navItemForPath(location.pathname);

  return (
    <TopBar
      start={
        <>
          <IconButton
            size="sm"
            label={navCollapsed ? t("nav.expand") : t("nav.collapse")}
            aria-expanded={!navCollapsed}
            onClick={onToggleNav}
          >
            {/* Panel icons mirror with direction via the shared icon-flip rule. */}
            {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </IconButton>
          <h1 className="shell-topbar-title">{active !== undefined ? t(active.titleKey) : t("app.name")}</h1>
        </>
      }
      end={
        <>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("topbar.languageSwitchTo")}
            onClick={() => {
              setLocale(locale === "ar" ? "en" : "ar");
            }}
          >
            {t("topbar.language")}
          </Button>
          <ModeToggle />
          <AccountMenu />
        </>
      }
    />
  );
}
